'use client';

import { useRouter } from 'next/navigation';
import { useContext, useEffect, useState } from 'react';

import { ACTION_STATUS_SUCCESS } from '@/actions';
import { getPromotionsData } from '@/actions/new-car/promotions';
import type { ResponseBody } from '@/apis/server/misc/v1/auto-complete';
import type {
  CamelCasePromotionList,
} from '@/apis/server/new-car/v1/promotions';
import {
  isCategoryType,
  isNewCarFuelType,
} from '@/apis/server/new-car/v1/utils';
import {
  createQueryString,
} from '@/app/(routes)/listing/_components/page-content/utils';
import type { OnSearchData } from '@/app/(routes)/listing/types';
import {
  searchAutoCompleteList,
} from '@/components/search-filter/auto-complete/configs';
import {
  filterModalAccordionCategoriesConfig,
  filterModalCategoriesConfig,
  searchBarSearchOptionConfig,
} from '@/components/search-filter/configs';
import {
  ENGINE_CAP_SELECT_OPTION_VALUE_661_TO_1600,
  ENGINE_CAP_SELECT_OPTION_VALUE_1601_TO_2000,
  ENGINE_CAP_SELECT_OPTION_VALUE_2001_TO_3000,
  ENGINE_CAP_SELECT_OPTION_VALUE_LESS_THAN_661,
  ENGINE_CAP_SELECT_OPTION_VALUE_MORE_THAN_3000,
} from '@/components/search-filter/constants';
import { FilterLabelList } from '@/components/search-filter/filter-label-list';
import type {
  FilterValue,
} from '@/components/search-filter/filter-label-list/types';
import { FilterModal } from '@/components/search-filter/filter-modal';
import {
  defaultCheckboxValue,
  defaultChoiceValue,
  defaultRangeValue,
  defaultSelectValue,
} from '@/components/search-filter/filter-modal/configs';
import { SearchBar } from '@/components/search-filter/search-bar';
import {
  AUTHORISED_DISTRIBUTORS,
  FILTER_TYPE_NAME_CATEGORY,
  FILTER_TYPE_NAME_COE_TYPE,
  FILTER_TYPE_NAME_COUNTRY,
  FILTER_TYPE_NAME_DEALER_TYPE,
  FILTER_TYPE_NAME_DEPRECIATION,
  FILTER_TYPE_NAME_DRIVE_RANGE,
  FILTER_TYPE_NAME_ENERGY_CONSUPTION,
  FILTER_TYPE_NAME_ENGINE_CAP,
  FILTER_TYPE_NAME_FUEL_CONSUMPTION,
  FILTER_TYPE_NAME_FUEL_TYPE,
  FILTER_TYPE_NAME_OMV,
  FILTER_TYPE_NAME_POWER_OUTPUT,
  FILTER_TYPE_NAME_PRICE_RANGE,
  FILTER_TYPE_NAME_TRANSIMISSION,
  PARALLEL_IMPORTERS,
  VEHICLE_VALUE_ELECTRIC_CARS,
} from '@/constants/global';
import { AppContext } from '@/context/app';
import { useDebouncedCallback } from '@/hooks/use-debounce-callback';
import { useFilterParams } from '@/hooks/use-filter-params';
import { joinSgcmMsSite } from '@/utils/url/join/public';

import styles from './styles.module.scss';
import type { Component } from './types';

export const SearchFilterSection: Component = ({
  defaultSearchData,
  brandList,
  isFilterModalOpen,
  submitButtonOption,
  simpleMode,
  onFilterClick,
  onModalSubmit,
  onSubmit,
  onLabelRemove,
  onLabelRemoveAll,
  onFilterModalClose,
}) => {
  const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState<boolean>(false);
  const [
    isIgnoreSearchInputChange,
    setIsIgnoreSearchInputChange,
  ] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useFilterParams(defaultSearchData);
  const [autoCompleteList] = useState(searchAutoCompleteList);
  const appContextData = useContext(AppContext);
  const router = useRouter();
  const [
    autoCompleteResults,
    setAutoCompleteResults,
  ] = useState<ResponseBody['data'] | []>([]);
  const [
    promotionList,
    setPromotionList,
  ] = useState<CamelCasePromotionList | null>(null);
  const [
    promotionResults,
    setPromotionResults,
  ] = useState<CamelCasePromotionList | null>(null);
  const [searchValue, setSearchValue] = useState(
    defaultSearchData.query.q ?? '',
  );
  const [carMakeValue, setCarMakeValue] = useState(
    defaultSearchData.query.make ?? '',
  );
  const [
    selectValues,
    setSelectValues,
  ] = useState(defaultSelectValue(defaultSearchData));
  const [
    rangeValues,
    setRangeValues,
  ] = useState(defaultRangeValue(defaultSearchData));
  const [
    choiceValues,
    setChoiceValues,
  ] = useState(defaultChoiceValue(defaultSearchData));
  const [
    checkboxValues,
    setCheckboxValues,
  ] = useState(defaultCheckboxValue(defaultSearchData));

  useEffect(() => {
    const fetchData = async () => {
      const promotionsActionResult = await getPromotionsData(new FormData());

      if (promotionsActionResult.status !== ACTION_STATUS_SUCCESS) {
        setPromotionList(null);
      } else {
        setPromotionList(promotionsActionResult.data.data);
      }
    };

    fetchData();
  }, []);

  const handleSearchAutoComplete = async () => {
    if (isIgnoreSearchInputChange
      || searchValue.length < 1
      || searchValue === defaultSearchData.query.q
    ) {
      setIsAutoCompleteOpen(false);
      setIsIgnoreSearchInputChange(false);
      return;
    }

    setAutoCompleteResults(
      autoCompleteList.filter(
        item => item
          .toLowerCase().search(searchValue.toLowerCase()) !== -1,
      ),
    );
    setPromotionResults(
      promotionList?.filter(
        item => item.title
          .toLowerCase().search(searchValue.toLowerCase()) !== -1,
      ) ?? null,
    );

    setIsAutoCompleteOpen(true);
  };

  const debouncedAutoComplete = useDebouncedCallback(
    () => {
      handleSearchAutoComplete();
    },
    500,
    {},
  );

  const handleSearchInputChange = async (v: string) => {
    setSearchValue(v);
    debouncedAutoComplete();
  };

  const handleSubmit = () => {
    const searchData: OnSearchData = {
      query: {
        q: searchValue || undefined,
        make: carMakeValue || undefined,
        page: undefined,
        dealerId: undefined,
      },
    };

    Object.values(rangeValues).forEach((v) => {
      switch (v.name) {
        case FILTER_TYPE_NAME_PRICE_RANGE:
          searchData.query.minPrice = +v.lowerValue.value || undefined;
          searchData.query.maxPrice = +v.upperValue.value || undefined;
          break;
        case FILTER_TYPE_NAME_DEPRECIATION:
          searchData.query.minDepreciation = +v.lowerValue.value || undefined;
          searchData.query.maxDepreciation = +v.upperValue.value || undefined;
          break;
        case FILTER_TYPE_NAME_DRIVE_RANGE:
          searchData.query.minDriveRange = +v.lowerValue.value || undefined;
          searchData.query.maxDriveRange = +v.upperValue.value || undefined;
          break;
        default:
          break;
      }
    });

    Object.values(choiceValues).forEach((v) => {
      switch (v.name) {
        case FILTER_TYPE_NAME_OMV:
          switch (v.optionValue.value) {
            case '>':
              if (v.inputValue) {
                searchData.query.minOmv = parseInt(v.inputValue, 10);
                searchData.query.maxOmv = undefined;
              }
              break;
            case '<':
              if (v.inputValue) {
                searchData.query.minOmv = undefined;
                searchData.query.maxOmv = parseInt(v.inputValue, 10);
              }
              break;
            case '=':
              if (v.inputValue) {
                searchData.query.minOmv = parseInt(v.inputValue, 10);
                searchData.query.maxOmv = parseInt(v.inputValue, 10);
              }
              break;
            default:
              searchData.query.minOmv = undefined;
              searchData.query.maxOmv = undefined;
              break;
          }
          break;
        case FILTER_TYPE_NAME_FUEL_CONSUMPTION:
          switch (v.optionValue.value) {
            case '>':
              if (v.inputValue) {
                searchData.query.minFuelEconomy = parseInt(v.inputValue, 10);
                searchData.query.maxFuelEconomy = undefined;
              }
              break;
            case '<':
              if (v.inputValue) {
                searchData.query.minFuelEconomy = undefined;
                searchData.query.maxFuelEconomy = parseInt(v.inputValue, 10);
              }
              break;
            case '=':
              if (v.inputValue) {
                searchData.query.minFuelEconomy = parseInt(v.inputValue, 10);
                searchData.query.maxFuelEconomy = parseInt(v.inputValue, 10);
              }
              break;
            default:
              searchData.query.minFuelEconomy = undefined;
              searchData.query.maxFuelEconomy = undefined;
              break;
          }
          break;
        case FILTER_TYPE_NAME_POWER_OUTPUT:
          switch (v.optionValue.value) {
            case '>':
              if (v.inputValue) {
                searchData.query.minPowerOutput = parseInt(v.inputValue, 10);
                searchData.query.maxPowerOutput = undefined;
              }
              break;
            case '<':
              if (v.inputValue) {
                searchData.query.minPowerOutput = undefined;
                searchData.query.maxPowerOutput = parseInt(v.inputValue, 10);
              }
              break;
            case '=':
              if (v.inputValue) {
                searchData.query.minPowerOutput = parseInt(v.inputValue, 10);
                searchData.query.maxPowerOutput = parseInt(v.inputValue, 10);
              }
              break;
            default:
              searchData.query.minPowerOutput = undefined;
              searchData.query.maxPowerOutput = undefined;
              break;
          }
          break;
        case FILTER_TYPE_NAME_ENERGY_CONSUPTION:
          switch (v.optionValue.value) {
            case '>':
              if (v.inputValue) {
                searchData.query.minEnergyConsumption = parseInt(
                  v.inputValue,
                  10,
                );
                searchData.query.maxEnergyConsumption = undefined;
              }
              break;
            case '<':
              if (v.inputValue) {
                searchData.query.minEnergyConsumption = undefined;
                searchData.query.maxEnergyConsumption = parseInt(
                  v.inputValue,
                  10,
                );
              }
              break;
            case '=':
              if (v.inputValue) {
                searchData.query.minEnergyConsumption = parseInt(
                  v.inputValue,
                  10,
                );
                searchData.query.maxEnergyConsumption = parseInt(
                  v.inputValue,
                  10,
                );
              }
              break;
            default:
              searchData.query.minEnergyConsumption = undefined;
              searchData.query.maxEnergyConsumption = undefined;
              break;
          }
          break;
        default:
          break;
      }
    });

    Object.values(checkboxValues).forEach((checkbox) => {
      if (checkbox.name === 'Vehicle Type') {
        searchData.query.vehicleType = Object.values(checkbox.values)
          .filter(v => v.checked)
          .map(v => v.value)
          .join(',') || undefined;
      }
    });

    Object.values(selectValues).forEach((v) => {
      switch (v.name) {
        case FILTER_TYPE_NAME_TRANSIMISSION:
          searchData.query.transmission = v.optionValue.value.toString()
          || undefined;
          break;
        case FILTER_TYPE_NAME_FUEL_TYPE:
          if (isNewCarFuelType(v.optionValue.value)) {
            if (v.optionValue.value === VEHICLE_VALUE_ELECTRIC_CARS) {
              searchData.query.fuelType = v.optionValue.value;
            } else {
              searchData.query.fuelType = v.optionValue.value;
              searchData.query.minDriveRange = undefined;
              searchData.query.maxDriveRange = undefined;
              searchData.query.minEnergyConsumption = undefined;
              searchData.query.maxEnergyConsumption = undefined;
            }
          } else {
            searchData.query.fuelType = undefined;
          }
          break;
        case FILTER_TYPE_NAME_CATEGORY:
          if (isCategoryType(v.optionValue.value)) {
            searchData.query.category = v.optionValue.value
            || undefined;
          }
          break;
        case FILTER_TYPE_NAME_COE_TYPE:
          searchData.query.coeCategory = v.optionValue.value.toString()
          || undefined;
          break;
        case FILTER_TYPE_NAME_DEALER_TYPE:
          if (v.optionValue.value === AUTHORISED_DISTRIBUTORS
            || v.optionValue.value === PARALLEL_IMPORTERS) {
            searchData.query.dealerType = v.optionValue.value;
          }
          break;
        case FILTER_TYPE_NAME_COUNTRY:
          searchData.query.country = v.optionValue.value.toString()
          || undefined;
          break;
        case FILTER_TYPE_NAME_ENGINE_CAP:
          switch (v.optionValue.value) {
            case ENGINE_CAP_SELECT_OPTION_VALUE_LESS_THAN_661:
              searchData.query.minEngineCapacity = undefined;
              searchData.query.maxEngineCapacity = 661;
              break;
            case ENGINE_CAP_SELECT_OPTION_VALUE_661_TO_1600:
              searchData.query.minEngineCapacity = 661;
              searchData.query.maxEngineCapacity = 1600;
              break;
            case ENGINE_CAP_SELECT_OPTION_VALUE_1601_TO_2000:
              searchData.query.minEngineCapacity = 1601;
              searchData.query.maxEngineCapacity = 2000;
              break;
            case ENGINE_CAP_SELECT_OPTION_VALUE_2001_TO_3000:
              searchData.query.minEngineCapacity = 2001;
              searchData.query.maxEngineCapacity = 3000;
              break;
            case ENGINE_CAP_SELECT_OPTION_VALUE_MORE_THAN_3000:
              searchData.query.minEngineCapacity = 3000;
              searchData.query.maxEngineCapacity = undefined;
              break;
            default:
              searchData.query.minEngineCapacity = undefined;
              searchData.query.maxEngineCapacity = undefined;
              break;
          }
          break;
        default:
          break;
      }
    });

    const search = createQueryString(searchData);

    router.push(joinSgcmMsSite(`/new-cars/listing/search?${search}`).toString());
    appContextData?.setIsFilterModalOpen(false);
  };

  const handleFilterModalSubmit = () => {
    handleSubmit();
    onModalSubmit?.(
      searchValue,
      carMakeValue,
      selectValues,
      rangeValues,
      choiceValues,
      checkboxValues,
    );
  };

  const handleSearchSubmit = () => {
    handleSubmit();
    onSubmit?.(
      searchValue,
      carMakeValue,
      selectValues,
      rangeValues,
      choiceValues,
      checkboxValues,
    );
    setIsAutoCompleteOpen(false);
  };

  const handleLabelRemove = (i: number, value: FilterValue) => {
    onLabelRemove?.(i, value);
    setFilterValues(filterValues.filter((v, idx) => i !== idx));
  };

  const handleLabelRemoveAll = () => {
    onLabelRemoveAll?.(filterValues);
    setFilterValues([]);
  };

  const handleFilterModalClose = () => {
    appContextData?.setIsFilterModalOpen(false);
    onFilterModalClose?.();
  };

  const handleCarMakeChange = (value: string) => {
    setCarMakeValue(value);
  };

  useEffect(() => {
    setIsAutoCompleteOpen(false);
  }, [defaultSearchData]);

  useEffect(() => {
    setChoiceValues(defaultChoiceValue(defaultSearchData));
    setRangeValues(defaultRangeValue(defaultSearchData));
    setSelectValues(defaultSelectValue(defaultSearchData));
    setCheckboxValues(defaultCheckboxValue(defaultSearchData));
  }, [defaultSearchData]);

  return (
    <>
      <FilterModal
        accordionCategories={filterModalAccordionCategoriesConfig}
        autoCompleteOptions={autoCompleteResults}
        availableYear=""
        brandList={brandList}
        carMakeValue={carMakeValue}
        categories={filterModalCategoriesConfig}
        checkboxValues={checkboxValues}
        choiceValues={choiceValues}
        defaultFilterValues={filterValues}
        defaultSearchData={defaultSearchData}
        isAutoCompleteOpen={isAutoCompleteOpen}
        isOpen={isFilterModalOpen}
        rangeValues={rangeValues}
        searchValue={searchValue}
        selectValues={selectValues}
        setCheckboxValues={setCheckboxValues}
        setChoiceValues={setChoiceValues}
        setRangeValues={setRangeValues}
        setSearchValue={setSearchValue}
        setSelectValues={setSelectValues}
        onAutoCompleteBoxOpen={setIsAutoCompleteOpen}
        onCarMakeChange={handleCarMakeChange}
        onClose={handleFilterModalClose}
        onIgnoreSearchInputChange={setIsIgnoreSearchInputChange}
        onSearchInputChange={handleSearchInputChange}
        onSubmit={handleFilterModalSubmit} />
      <SearchBar
        autoCompleteOptions={autoCompleteResults}
        brandList={brandList}
        carMakeValue={carMakeValue}
        checkboxValues={checkboxValues}
        isAutoCompleteOpen={isAutoCompleteOpen}
        promotionOptions={promotionResults}
        rangeValues={rangeValues}
        searchInputOption={searchBarSearchOptionConfig}
        searchValue={searchValue}
        selectValues={selectValues}
        setCheckboxValues={setCheckboxValues}
        setRangeValues={setRangeValues}
        setSearchValue={setSearchValue}
        setSelectValues={setSelectValues}
        simpleMode={simpleMode}
        submitButtonOption={submitButtonOption}
        showPromotions
        onAutoCompleteBoxOpen={setIsAutoCompleteOpen}
        onCarMakeChange={handleCarMakeChange}
        onFilterClick={onFilterClick}
        onIgnoreSearchInputChange={setIsIgnoreSearchInputChange}
        onSearchInputChange={handleSearchInputChange}
        onSubmit={handleSearchSubmit} />
      <div className={`${filterValues.length > 0 ? styles.containerFilterLabeList : styles.displayNone}`}>
        <FilterLabelList
          hasRemoveAll={filterValues.length > 0}
          values={filterValues}
          onRemove={handleLabelRemove}
          onRemoveAll={handleLabelRemoveAll} />
      </div>
    </>
  );
};
